// Шапка полноширинного представления: отборы и календари слева, действия справа.
//
// Заведена 25.08.2026, потому что каждое представление рисовало шапку само и по-своему.
// Замер по снимкам живого экрана: «Расход» — `px-5 py-3`, «Официально» — `px-4 py-2`,
// ведомости — свой третий вариант, деталь раздела — четвёртый. Разница в четыре точки не
// видна на одном экране и отлично видна при переключении вкладок внутри «Расчёта»: заголовки
// и поля прыгают влево-вправо и вверх-вниз на каждом переключении. Именно это человек
// называет «кривовато», не умея показать пальцем.
//
// Высота задана минимумом, а не фиксирована: в шапке бывает две строки отборов, и жёсткая
// высота обрезала бы вторую.

import type { ReactNode } from 'react';

import * as Label from '@/shared/ui/label';
import { cn } from '@/utils/cn';

export interface ToolbarProps {
  /** Отборы, календари, переключатель представлений — то, что сужает выборку. */
  children: ReactNode;
  /** Действия над всем представлением: выгрузка, заведение, закрытие периода. */
  actions?: ReactNode;
  className?: string;
}

export function Toolbar({ children, actions, className }: ToolbarProps) {
  return (
    <header
      className={cn(
        // По НИЖНЕЙ линии, а не по центру. В шапке рядом стоят и голые кнопки, и поля с
        // подписью сверху: при выравнивании по центру кнопка без подписи повисает выше
        // соседнего поля, потому что поле выше на строку подписи. По нижней линии все органы
        // управления стоят на одной прямой независимо от того, подписаны они или нет.
        'flex min-h-14 flex-wrap items-end justify-between gap-x-5 gap-y-3',
        'border-b border-stroke-soft-200 bg-bg-white-0 px-5 py-3',
        // В узком окне поле у всех поверхностей одно — 16 точек: список, карточка, панель и
        // эта шапка стоят друг под другом в одной колонке, и разные поля читаются как
        // дрожание текста при переходе между ними.
        'узко:px-4',
        className,
      )}
    >
      <div className='flex min-w-0 flex-wrap items-end gap-x-5 gap-y-3'>{children}</div>
      {actions !== undefined && (
        // `ml-auto` держит действия у правого края ДАЖЕ при переносе. Без него шапка со
        // свёрнутым реестром ведомостей роняла «Создать ведомости на период» на вторую
        // строку и прижимала к левому краю, вплотную к названию открытой ведомости: кнопка
        // оказывалась посреди пустой строки и читалась как часть подписи рядом.
        <div className='ml-auto flex shrink-0 items-end gap-2'>{actions}</div>
      )}
    </header>
  );
}

/**
 * Подписанная величина в шапке: подпись сверху мелким, значение под ней.
 *
 * Нужна ради ОДНОЙ базовой линии. Пока каждый рисовал подпись сам, в шапке «Официально»
 * подпись «Официальный период» стояла на восемь точек выше подписи «Связанная ведомость» —
 * два соседних поля выглядели съехавшими относительно друг друга.
 */
export function ToolbarField({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  // Подпись рисуется ТЕМ ЖЕ примитивом, что и подпись поля выбора рядом. Пока здесь стоял свой
  // мелкий серый текст, в одной шапке соседствовали две разные подписи: «Официальный период»
  // над полем выбора и «Связанная ведомость» рядом отличались и кеглем, и цветом, и высотой
  // строки — то есть съезжали друг относительно друга на пару точек. Это ровно тот случай,
  // когда «вроде всё на месте, а выглядит криво».
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)}>
      <Label.Root>{label}</Label.Root>
      <div className='flex min-h-9 items-center text-label-sm text-text-strong-950'>{children}</div>
    </div>
  );
}
